import { useState } from "react";
import { motion } from "framer-motion";
import { User, Store, Phone, Mail, Edit3, LogOut, Save, Loader2, ShieldCheck, Camera } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import ReceiptLogoSettings from "../components/ReceiptLogoSettings";


export default function ProfilePage() {
    const { user, refreshUser, logout } = useAuth();
    const navigate = useNavigate();
    const [edit, setEdit] = useState(false);
    const [form, setForm] = useState({
        warung_name: user?.warung_name || "",
        owner_name: user?.owner_name || "",
        phone: user?.phone || "",
    });
    const [saving, setSaving] = useState(false);
    const [upgradeForm, setUpgradeForm] = useState({ shopName: "", ktpPhoto: null, selfiePhoto: null });
    const [upgradeSaving, setUpgradeSaving] = useState(false);
    const upgradeStatus = user?.mitraUpgradeStatus || user?.upgradeRequestStatus;

    const onLogout = async (allSessions = false) => {
        if (allSessions && !window.confirm("Keluar dari semua perangkat?")) return;
        try {
            await logout(allSessions);
            navigate("/login");
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const save = async () => {
        if (form.owner_name.trim().length < 2 || form.owner_name.trim().length > 120) return toast.error("Nama harus 2–120 karakter");
        if (form.phone && !/^\+?\d{8,15}$/.test(form.phone.trim())) return toast.error("Nomor telepon harus 8–15 digit");
        setSaving(true);
        try {
            await api.patch("/users/me", {
                fullName: form.owner_name.trim(),
                ...(user?.role === "mitra" ? { shopName: form.warung_name.trim() } : {}),
                ...(form.phone.trim() ? { phone: form.phone.trim() } : {}),
                
            });
            toast.success("Profil tersimpan");
            await refreshUser();
            setEdit(false);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const selectUpgradeImage = (field) => (event) => {
        const image = event.target.files?.[0] || null;
        if (!image) return;
        if (!['image/jpeg', 'image/png', 'image/webp'].includes(image.type) || image.size > 5 * 1024 * 1024) {
            toast.error("Gunakan foto JPG, PNG, atau WEBP dengan ukuran maksimal 5 MB.");
            event.target.value = "";
            return;
        }
        setUpgradeForm((current) => ({ ...current, [field]: image }));
    };

    const requestMitraUpgrade = async (event) => {
        event.preventDefault();
        if (upgradeForm.shopName.trim().length < 2) return toast.error("Isi nama warung Anda.");
        if (!upgradeForm.ktpPhoto || !upgradeForm.selfiePhoto) return toast.error("Foto KTP dan selfie autentik wajib diunggah.");
        setUpgradeSaving(true);
        try {
            const payload = new FormData();
            payload.append("shopName", upgradeForm.shopName.trim());
            payload.append("ktpPhoto", upgradeForm.ktpPhoto);
            payload.append("selfiePhoto", upgradeForm.selfiePhoto);
            await api.post("/users/me/upgrade-to-mitra", payload);
            toast.success("Pengajuan upgrade dikirim untuk diverifikasi admin.");
            await refreshUser();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setUpgradeSaving(false);
        }
    };

    return (
        <div className="space-y-5">
            <h1 className="font-heading font-bold text-xl tracking-tight">Profil Warung</h1>

            <motion.div
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                className="rounded-3xl bg-gradient-to-br from-brand-deep to-[#1E3A8A] dark:from-[#0a2540] dark:to-[#063b2e] p-6 text-white relative overflow-hidden"
            >
                <div className="absolute -top-10 -right-10 h-40 w-40 rounded-full bg-brand-green/20 blur-3xl" />
                <div className="relative flex items-center gap-4">
                    <div className="h-16 w-16 rounded-2xl bg-white/20 backdrop-blur-md flex items-center justify-center font-heading font-bold text-2xl">
                        {user?.warung_name?.[0] || "W"}
                    </div>
                    <div>
                        <div className="font-heading font-bold text-lg">{user?.warung_name}</div>
                        <div className="text-sm text-white/80">{user?.owner_name}</div>
                        <div className="inline-block text-[10px] uppercase tracking-widest mt-1 px-2 py-0.5 rounded-full bg-brand-green/30 text-brand-greenGlow font-semibold">
                            {{ admin: "Admin", super_admin: "Super Admin", mitra: "Mitra", customer: "Pelanggan" }[user?.role] || user?.role}
                        </div>
                        <div className="text-sm text-white/80">
                            Saldo: {Number(user?.wallet?.balance ?? 0).toLocaleString("id-ID")} {user?.wallet?.currency}
                        </div>
                    </div>
                </div>
            </motion.div>

            <div className="rounded-2xl bg-card border border-border p-5 space-y-4">
                <div className="flex items-center justify-between">
                    <div className="font-heading font-semibold">Detail Akun</div>
                    {!edit ? (
                        <Button data-testid="edit-profile-btn" size="sm" variant="ghost" onClick={() => { setForm({ warung_name: user?.warung_name || "", owner_name: user?.owner_name || "", phone: user?.phone || "" }); setEdit(true); }}>
                            <Edit3 className="h-4 w-4 mr-1" /> Edit
                        </Button>
                    ) : (
                        <Button disabled={saving} data-testid="cancel-edit-btn" size="sm" variant="ghost" onClick={() => { setEdit(false); setForm({ warung_name: user.warung_name, owner_name: user.owner_name, phone: user.phone }); }}>
                            Batal
                        </Button>
                    )}
                </div>

                {!edit ? (
                    <div className="space-y-3 text-sm">
                        <Info icon={<Store className="h-4 w-4" />} k="Nama Warung" v={user?.warung_name} />
                        <Info icon={<User className="h-4 w-4" />} k="Pemilik" v={user?.owner_name} />
                        <Info icon={<Phone className="h-4 w-4" />} k="No. HP" v={user?.phone} />
                        <Info icon={<Mail className="h-4 w-4" />} k="Email" v={user?.email} />
                    </div>
                ) : (
                    <div className="space-y-3">
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wider">Nama Warung</Label>
                            <Input disabled={saving || user?.role !== "mitra"} maxLength={120} data-testid="profile-warung-input" value={form.warung_name} onChange={(e) => setForm({ ...form, warung_name: e.target.value })} className="h-12 rounded-xl mt-1.5" />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wider">Pemilik</Label>
                            <Input disabled={saving} maxLength={120} data-testid="profile-owner-input" value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} className="h-12 rounded-xl mt-1.5" />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wider">No. HP</Label>
                            <Input disabled={saving} type="tel" data-testid="profile-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-12 rounded-xl mt-1.5" />
                        </div>
                        <Button onClick={save} disabled={saving} className="w-full bg-brand-green hover:bg-brand-greenDark text-white rounded-xl min-h-[48px] font-semibold" data-testid="profile-save-btn">
                            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Save className="h-4 w-4 mr-1.5" /> Simpan</>)}
                        </Button>
                    </div>
                )}
            </div>

            {user?.role === 'mitra' && user?.id && <ReceiptLogoSettings key={user.id} user={user} />}
            {user?.role === "customer" && (
                <section className="rounded-2xl border border-brand-green/30 bg-brand-green/5 p-5 space-y-4">
                    <div className="flex gap-3">
                        <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-brand-green" />
                        <div><h2 className="font-heading font-semibold">Upgrade ke Mitra</h2><p className="mt-1 text-sm text-muted-foreground">Lengkapi identitas untuk aktivasi akun mitra oleh admin.</p></div>
                    </div>
                    {upgradeStatus ? <p className="rounded-lg bg-background/80 p-3 text-sm">Status pengajuan: <strong>{upgradeStatus}</strong></p> : (
                        <form onSubmit={requestMitraUpgrade} className="space-y-3">
                            <div><Label htmlFor="upgrade-shop-name">Nama warung</Label><Input id="upgrade-shop-name" value={upgradeForm.shopName} maxLength={120} disabled={upgradeSaving} onChange={(event) => setUpgradeForm((current) => ({ ...current, shopName: event.target.value }))} placeholder="Nama warung Anda" className="mt-1" /></div>
                            <ImageUpload id="upgrade-ktp" label="Foto KTP" file={upgradeForm.ktpPhoto} onChange={selectUpgradeImage("ktpPhoto")} disabled={upgradeSaving} />
                            <ImageUpload id="upgrade-selfie" label="Selfie autentik" file={upgradeForm.selfiePhoto} onChange={selectUpgradeImage("selfiePhoto")} disabled={upgradeSaving} capture />
                            <Button type="submit" disabled={upgradeSaving} className="w-full bg-brand-green text-white hover:bg-brand-greenDark">{upgradeSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Kirim Pengajuan Upgrade"}</Button>
                        </form>
                    )}
                </section>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Button variant="outline" onClick={() => navigate("/statistik")} className="rounded-xl min-h-[56px] flex-col gap-1" data-testid="goto-stats-from-profile">
                    <span className="font-heading font-bold">Statistik</span>
                    <span className="text-xs text-muted-foreground">Detail penjualan</span>
                </Button>
                {["admin", "super_admin"].includes(user?.role) && (
                    <Button variant="outline" onClick={() => navigate("/admin")} className="rounded-xl min-h-[56px] flex-col gap-1" data-testid="goto-admin-btn">
                        <span className="font-heading font-bold">Admin Panel</span>
                        <span className="text-xs text-muted-foreground">Kelola mitra</span>
                    </Button>
                )}
            </div>

            <Button
                data-testid="profile-logout-btn"
                onClick={() => onLogout(false)}
                variant="outline"
                className="w-full rounded-xl min-h-[52px] text-rose-500 border-rose-500/30 hover:bg-rose-500/5 hover:text-rose-600"
            >
                <ut className="h-4 w-4 mr-1.5" /> Logout
            </Button>
            <Button variant="ghost" onClick={() => onLogout(true)} className="w-full text-destructive">Keluar dari semua perangkat</Button>
        </div>
    );
}

function ImageUpload({ id, label, file, onChange, disabled, capture = false }) {
    return <div><Label htmlFor={id}>{label}</Label><label htmlFor={id} className="mt-1 flex min-h-12 cursor-pointer items-center gap-2 rounded-xl border border-dashed border-border bg-background px-3 text-sm hover:border-brand-green/60"><Camera className="h-4 w-4 text-muted-foreground" /><span className="min-w-0 truncate">{file?.name || "Pilih foto"}</span></label><input id={id} className="sr-only" type="file" accept="image/jpeg,image/png,image/webp" capture={capture ? "user" : undefined} onChange={onChange} disabled={disabled} /></div>;
}

function Info({ icon, k, v }) {
    return (
        <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-secondary flex items-center justify-center text-muted-foreground">{icon}</div>
            <div className="flex-1">
                <div className="text-xs text-muted-foreground">{k}</div>
                <div className="font-semibold text-sm">{v}</div>
            </div>
        </div>
    );
}
